<template>
  <master-layout v-model="activeStep" :navigation="steps">
    <template #header>
      <div class="d-flex align-center">
        <div class="caption ellipsis-1 mr-2">
          <skeleton-loader v-if="!accommodationObject" inline width="300px" class="mr-2" type="caption" />
          <span v-else>{{ accommodationObject.commonInformation.name[0].value }}</span
          >. Анкeта
          <span v-if="!isQuestionnaireSent" class="text-secondary"> | Черновик </span>
        </div>

        <v-spacer />

        <e-btn 
          v-if="isQuestionnaireSent"
          variant="primary"
          size="M"
          class="mr-2"
          :prepend-icon-src="$vuetify.display.smAndUp ? 'editPen' : undefined"
          :loading="isEditQuestionnaireLoading"
          @click="startEditQuestionnaire"
        >
          <span v-if="$vuetify.display.smAndUp">Редактировать</span>
          <e-icon v-else name="editPen" />
        </e-btn>

        <confirmation-dialog
          v-else-if="questionnaire?.hasParent && isQuestionnaireDraft"
          :action="cancelEditQuestionnaire"
          confirm-text="Да"
          cancel-text="Нет"
        >
          <template #activator>
            <e-btn
              variant="primary"
              size="M"
              class="mr-2"
              :prepend-icon-src="$vuetify.display.smAndUp ? 'cancelEdit' : undefined"
            >
              <span v-if="$vuetify.display.smAndUp">Отменить редактирование</span>
              <e-icon v-else name="cancelEdit" />
            </e-btn>
          </template>

          <div class="h3 mb-6">Вы уверены, что хотите отменить редактирование?</div>
          <div class="paragraph-l text-secondary">Данные черновика не будут сохранены</div>
        </confirmation-dialog>

        <confirmation-dialog :action="back" :auto-confirm="isQuestionnaireSent" confirm-text="Прервать">
          <template #activator>
            <e-btn variant="context" size="M" append-icon-src="closeSvg">Закрыть</e-btn>
          </template>

          <div class="h3 mb-6">Прерывание работы мастера</div>
          <div class="paragraph-l text-secondary">Все введённые настройки будут сохранены.</div>
        </confirmation-dialog>
      </div>

      <e-alert v-if="isQuestionnaireSent" variant="warning" class="mt-4">
        <div class="paragraph-m_h font-600">Данные могут отличаться от опубликованных на сайте</div>

        <div class="paragraph-s_h text-secondary">
          Это может в случае, если вы вносили изменения в данные вашего объекта размещения через
          <router-link :to="{ name: 'Support' }" class="text-primary">Отдел контента</router-link>
        </div>
      </e-alert>
    </template>

    <error-boundary error-key="contentServiceQuestionnaireAnswers">
      <template v-if="isLoaded">
        <common-information v-if="activeStep === 0" />
        <rooms-information v-if="activeStep === 1" />
        <infrastructure v-if="activeStep === 2" />
        <restaurants-and-event-halls v-if="activeStep === 3" />
        <services-information v-if="activeStep === 4" />
        <treatment v-if="activeStep === 5" />
        <conference-services v-if="activeStep === 6" />
        <send-questionnaire v-if="activeStep === 7" />
      </template>

      <e-loader v-else :size="60" class="mx-auto mt-10 text-primary" />
    </error-boundary>
  </master-layout>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import type { NavigationItem } from '@/core/types/components/components';
import SkeletonLoader from '@/core/components/interface/SkeletonLoader.vue';
import ConfirmationDialog from '@/core/components/modals/ConfirmationDialog.vue';
import { EAlert, EBtn, EIcon, ELoader } from '@/core/components/ui';
import { useFetch } from '@/core/composables/useFetch';
import { useRouterBack } from '@/core/composables/useRouterBack';
import MasterLayout from '@/core/layouts/components/master/MasterLayout.vue';
import { RouteNames } from '@/core/plugins/router/routeNames';
import CommonInformation from '@/parts/content/components/CommonInformation.vue';
import ConferenceServices from '@/parts/content/components/ConferenceServices.vue';
import Infrastructure from '@/parts/content/components/Infrastructure.vue';
import RestaurantsAndEventHalls from '@/parts/content/components/RestaurantsAndEventHalls.vue';
import RoomsInformation from '@/parts/content/components/RoomsInformation.vue';
import SendQuestionnaire from '@/parts/content/components/SendQuestionnaire.vue';
import ServicesInformation from '@/parts/content/components/ServicesInformation.vue';
import Treatment from '@/parts/content/components/treatment/Treatment.vue';
import { useContentStore } from '@/parts/content/store';
import ErrorBoundary from '@/parts/errors/components/ErrorBoundary.vue';
import { useAccommodationObjectsStore } from '@/parts/objects/store/accommodationObjects';

const { getActiveObject, accommodationObject } = useAccommodationObjectsStore();
const {
  getContentServiceDictionaries,
  getQuestionnaire,
  getQuestionnaireAnswers,
  completedSteps,
  activeStep,
  questionnaire,
  isQuestionnaireSent,
  isQuestionnaireDraft,
  clearQuestionnaire,
  editQuestionnaire,
  cancelEditQuestionnaire,
} = useContentStore();

const { routerBack } = useRouterBack();

const back = () => {
  routerBack({ name: RouteNames.ObjectInfo, params: { id: accommodationObject.value?.cid } });
};

const isEditQuestionnaireLoading = ref(false);
const startEditQuestionnaire = async () => {
  isEditQuestionnaireLoading.value = true;

  await editQuestionnaire();

  isEditQuestionnaireLoading.value = false;
};

const isLoaded = ref(false);
useFetch(async () => {
  clearQuestionnaire();

  const promises = [getActiveObject(), getContentServiceDictionaries(), getQuestionnaire()];

  await Promise.all(promises);

  await getQuestionnaireAnswers();

  isLoaded.value = true;
});

watch(activeStep, () => {
  window.scrollTo(0, 0);
});

const steps = computed<NavigationItem[]>(() => [
  {
    title: 'Основная информация',
    icon: 'info',
  },
  {
    title: 'Номера',
    disabled: completedSteps.value < 0,
    icon: 'room',
  },
  {
    title: 'Инфраструктура',
    disabled: completedSteps.value < 1,
    icon: 'shop',
  },
  {
    title: 'Питание',
    disabled: completedSteps.value < 2,
    icon: 'restaurant',
  },
  {
    title: 'Услуги и развлечения',
    disabled: completedSteps.value < 3,
    icon: 'starMagic',
  },
  {
    title: 'Лечение',
    disabled: completedSteps.value < 4,
    icon: 'treatment',
  },
  {
    title: 'Конференц-услуги',
    disabled: completedSteps.value < 5,
    icon: 'presentionChart',
  },
  {
    title: 'Отправка анкеты',
    disabled: completedSteps.value < 6,
    icon: 'messageSend',
  },
]);
</script>

<style scoped lang="scss"></style>
